import React, { createContext, useState, useEffect, useCallback, ReactNode, useContext } from 'react';
import { useAuth } from './AuthProvider';
import { getMyServiceProviderProfile } from '../services/ServiceProviderProfileService';
import { IServiceProviderProfile } from '../interfaces/heavyDutyMachinery/IServiceProviderProfile';

interface ServiceProviderProfileContextProps {
  serviceProviderProfile: IServiceProviderProfile | undefined;
  hasServiceProviderProfile: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

interface ServiceProviderProfileProviderProps {
  children: ReactNode;
}

const ServiceProviderProfileContext = createContext<ServiceProviderProfileContextProps | undefined>(undefined);

/**
 * Single source of truth for "does the signed-in user have a
 * ServiceProviderProfile yet?" -- same role, and same shape, as
 * HostProfileProvider, but for the independent, unrelated axis Fase 13's
 * architecture doc describes (a user can be a Host, a ServiceProvider, or
 * both, with no shared gate between the two). Lets ServiceProviderOnboardingPage
 * redirect away once a profile already exists, same reasoning as
 * HostOnboardingPage's own hasHostProfile guard.
 */
export const ServiceProviderProfileProvider: React.FC<ServiceProviderProfileProviderProps> = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [serviceProviderProfile, setServiceProviderProfile] = useState<IServiceProviderProfile | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  // Same render-time sync as HostProfileProvider's identical comment -- a
  // fresh login flips isAuthenticated synchronously, one render before the
  // effect below gets a chance to re-fetch and reset `loading`.
  const [syncedFor, setSyncedFor] = useState(isAuthenticated);
  if (isAuthenticated !== syncedFor) {
    setSyncedFor(isAuthenticated);
    setLoading(true);
  }

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setServiceProviderProfile(undefined);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const profile = await getMyServiceProviderProfile();
      setServiceProviderProfile(profile);
    } catch {
      setServiceProviderProfile(undefined);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, refresh]);

  return (
    <ServiceProviderProfileContext.Provider
      value={{ serviceProviderProfile, hasServiceProviderProfile: !!serviceProviderProfile, loading, refresh }}
    >
      {children}
    </ServiceProviderProfileContext.Provider>
  );
};

export const useServiceProviderProfile = (): ServiceProviderProfileContextProps => {
  const context = useContext(ServiceProviderProfileContext);
  if (!context) throw new Error('useServiceProviderProfile must be used within a ServiceProviderProfileProvider');
  return context;
};
